import { defineStore } from 'pinia';

import type { ContextRequirement } from '@lib/utils/context';

/** One Font Awesome class, or a pair drawn as a stacked icon with the second one subscripted. */
export type SidebarIcon = string | [string, string];

/**
 * When an entry shows: the context and mode it needs (left out otherwise), and the context
 * permissions it needs, each granted in one of its `context` keys (any active one without), see
 * useContext().can.
 */
export type SidebarContextGate = ContextRequirement & {
	contextPermissions?: string[];
};

/** Internal entry, rendered as a router-link. */
export type SidebarItemRouterType = SidebarContextGate & {
	/** Router path; also matched against the current route to open the group holding it. */
	to: string;
	icon: SidebarIcon;
	/** Key looked up under `sidebar.*`. */
	label: string;
	/** Final text, rendered as is instead of the translated `label`. For names that are data, not translations. */
	title?: string;
	/** Badge at the end of the row, a dot while the sidebar is minimized. Numbers add up on the group row. */
	badge_text?: string;
	/** Bootstrap colour of the badge, e.g. `danger`. Defaults to `primary`. */
	badge_color?: string;
	/** Draws the row as disabled. */
	disabled?: boolean;
	/** Permissions the entry needs, all of them, checked with auth.check(). Without them it is left out. */
	permissions?: string[];
	/** Interpolation values for the `label` lookup. */
	values?: {
		[key: string]: string | number;
	};
};

/** External entry, opened in a new tab. */
export type SidebarItemLinkType = SidebarContextGate & {
	/** Target URL, opened with target="_blank" and rel="noopener noreferrer". */
	href: string;
	/** Keep `window.opener` in the new tab, for a page of the same origin that reads the SPA session (Swagger UI). */
	opener?: boolean;
	/** Falls back to an outgoing-link icon when unset. */
	icon?: SidebarIcon;
	/** Key looked up under `sidebar.*`. */
	label: string;
	/** Final text, rendered as is instead of the translated `label`. For names that are data, not translations. */
	title?: string;
	/** Draws the row as disabled. */
	disabled?: boolean;
	/** Permissions the entry needs, all of them, checked with auth.check(). Without them it is left out. */
	permissions?: string[];
	/** Interpolation values for the `label` lookup. */
	values?: {
		[key: string]: string | number;
	};
};

/** Rule between two runs of entries. The flag itself is never read, and a divider that ends up leading,
 * trailing or next to another one - because what it separated is not shown - is dropped. */
export type SidebarItemDividerType = {
	divider: boolean;
};

/** Caption above a run of entries, not clickable. Left out once no entry of that run is shown. */
export type SidebarItemHeaderType = SidebarContextGate & {
	/** Key looked up under `sidebar.*`. */
	label: string;
	/** Final text, rendered as is instead of the translated `label`. For names that are data, not translations. */
	title?: string;
	/** Without one the caption starts at the padding edge. */
	icon?: SidebarIcon;
	/** Permissions the entry needs, all of them, checked with auth.check(). Without them it is left out. */
	permissions?: string[];
	/** Interpolation values for the `label` lookup. */
	values?: {
		[key: string]: string | number;
	};
};

/** Group of entries: inline while the sidebar is wide, a flyout while it is minimized. */
export type SidebarItemGroupType = SidebarContextGate & {
	/** Key looked up under `sidebar.*`, and the identity the open state is kept under. */
	label: string;
	/** Final text, rendered as is instead of the translated `label`. For names that are data, not translations. */
	title?: string;
	icon?: SidebarIcon;
	/** Permissions the entry needs, all of them, checked with auth.check(). Without them it is left out. */
	permissions?: string[];
	/** Set to false to always show the children; the group header is then not toggleable. Defaults to true. */
	collapsible?: boolean;
	/** Entries only, groups do not nest. Filtered by their own permissions, and the group goes once none are left. */
	children: Array<SidebarItemRouterType | SidebarItemLinkType>;
};

export type SidebarItemType =
	| SidebarItemRouterType
	| SidebarItemLinkType
	| SidebarItemDividerType
	| SidebarItemHeaderType
	| SidebarItemGroupType;

/** localStorage key the open groups are kept under. */
const OPEN_GROUPS_STORAGE_KEY = 'appSidebarOpenGroups';

/** The open groups of the last visit; anything unreadable starts over as empty. */
function loadOpenGroups(): Record<string, boolean> {
	try {
		return JSON.parse(globalThis.localStorage?.getItem(OPEN_GROUPS_STORAGE_KEY) ?? '{}');
	} catch {
		return {};
	}
}

export const useAppSidebarMenuStore = defineStore('appSidebarMenu', {
	state: () => ({
		/** The menu as the app handed it over, rendered top to bottom. */
		items: [] as SidebarItemType[],
		/** Groups are collapsed unless recorded open here, keyed by `label`; kept across reloads via localStorage. */
		open_groups: loadOpenGroups(),
	}),
	actions: {
		/** Replaces the whole menu. */
		set(items: Array<SidebarItemType>) {
			this.items = items;
		},
		/** Opens or closes one group. */
		toggleGroup(label: string) {
			this.open_groups[label] = !this.open_groups[label];
			this.persistOpenGroups();
		},
		/** Opens a group that is not open yet, e.g. because the route moved into it. */
		openGroup(label: string) {
			if (!this.open_groups[label]) {
				this.open_groups[label] = true;
				this.persistOpenGroups();
			}
		},
		/** Writes the open groups back to localStorage. */
		persistOpenGroups() {
			globalThis.localStorage?.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify(this.open_groups));
		},
	},
});
